"use client";

import { useEmbeddedInApp } from "@/contexts/EmbeddedInAppContext";
import { useState } from "react";

/**
 * A 401 (mount or submit) means the session expired — a dead end inside the
 * WebView, so the funnel shows the same recovery state the login step shows.
 * Checking the status specifically matters: a 502/network fault is a backend
 * outage, not an expired session, and degrades to the normal form instead,
 * same as outside the WebView. Shared by the adult-confirm and child-guardian
 * details steps so the state machine lives in one place.
 * Only where the app transfers sessions; elsewhere 401s are handled as on web.
 */
export function useEmbeddedSessionExpiry(mountStatus: number | null): {
  expired: boolean;
  /** Call with a submit response's status; returns whether it reported expiry. */
  reportSubmitStatus: (status: number) => boolean;
} {
  const { sessionTransfer } = useEmbeddedInApp();
  const [expiredOnSubmit, setExpiredOnSubmit] = useState(false);

  function reportSubmitStatus(status: number): boolean {
    if (sessionTransfer && status === 401) {
      setExpiredOnSubmit(true);
      return true;
    }
    return false;
  }

  return {
    expired: sessionTransfer && (mountStatus === 401 || expiredOnSubmit),
    reportSubmitStatus,
  };
}
